<template>
    <div class="animated fadeIn">
        <LoadingWidget
            v-if="initStatus != 1"
            :status="initStatus"
            @init="init"
        />
        <b-card v-else>
            <h5 class="mb-0">
                <i class="fa fa-fw fa-edit mr-2" />
                Reset Edit Emas
            </h5>
            <hr />
            <b-row>
                <b-col md="3">
                    <b-form-group label="No. Faktur Barang">
                        <b-form-input
                            v-model="filter.noFaktur"
                            placeholder="Masukkan No. Faktur"
                            maxlength="50"
                            @keydown.enter="getItems"
                        />
                    </b-form-group>
                </b-col>
                <b-col md="3">
                    <b-form-group label="Status">
                        <b-form-select
                            v-model="filter.status"
                            :options="optFilterStatus"
                        />
                    </b-form-group>
                </b-col>
                <b-col md="3">
                    <b-form-group label="Aksi">
                        <b-button variant="primary" @click="getItems()">
                            <i class="fa fa-search mr-2" />Cari
                        </b-button>
                        <b-button
                            class="ml-2"
                            variant="danger"
                            @click="resetFilter"
                        >
                            Reset
                        </b-button>
                    </b-form-group>
                </b-col>
            </b-row>
            <b-table
                bordered
                hover
                show-empty
                no-local-sorting
                :busy="isBusy"
                :items="items"
                :fields="fields"
                @sort-changed="onSortChanged"
                responsive
                class="mb-3"
            >
                <template v-slot:empty>
                    Tidak ada data request edit emas.
                </template>
                <template v-slot:cell(noFaktur)="row">
                    {{ row.item.no_faktur_pgi || "-" }}
                </template>
                <template v-slot:cell(oldItem)="row">
                    {{ row.item.item_kind || "-" }}
                    <div
                        class="small text-muted"
                        v-if="row.item.item_brand || row.item.item_type"
                    >
                        {{
                            row.item.item_brand
                                ? row.item.item_brand + " - "
                                : ""
                        }}{{ row.item.item_type || "-" }}
                    </div>
                </template>
                <template v-slot:cell(newItem)="row">
                    {{ row.item.new_item_kind || "-" }}
                    <div
                        class="small text-muted"
                        v-if="row.item.new_item_brand || row.item.new_item_type"
                    >
                        {{
                            row.item.new_item_brand
                                ? row.item.new_item_brand + " - "
                                : ""
                        }}{{ row.item.new_item_type || "-" }}
                    </div>
                </template>
                <template v-slot:cell(created)="row">
                    {{ $helper.parseDate(row.item.created_at) }},
                    {{ $helper.parseHourMinute(row.item.created_at) }}
                    <div
                        class="small font-weight-bold"
                        v-if="row.item.created_by"
                    >
                        {{ row.item.created_by }}
                    </div>
                </template>
                <template v-slot:cell(status)="row">
                    <b-badge :variant="getStatusBadge(row.item.status)[0]">{{
                        getStatusBadge(row.item.status)[1]
                    }}</b-badge>
                </template>
                <template v-slot:cell(action)="row">
                    <div
                        class="d-flex align-items-center justify-content-center"
                    >
                        <b-button
                            size="sm"
                            variant="primary"
                            v-b-tooltip.hover.top
                            title="Info"
                            @click="showInfo(row.item)"
                        >
                            <i class="fa fa-fw fa-info" />
                        </b-button>
                        <div v-if="canApprove(row.item)">
                            <b-button
                                size="sm"
                                class="ml-2"
                                variant="success"
                                v-b-tooltip.hover.top
                                title="Terima"
                                @click="acceptRequest(row.item)"
                            >
                                <i class="fa fa-fw fa-check" />
                            </b-button>
                            <b-button
                                size="sm"
                                class="ml-2"
                                variant="danger"
                                v-b-tooltip.hover.top
                                title="Tolak"
                                @click="rejectRequest(row.item)"
                            >
                                <i class="fa fa-fw fa-times" />
                            </b-button>
                        </div>
                    </div>
                </template>
            </b-table>
            <PageSizer
                :totalRows="totalRows"
                :page="currentPage"
                :options="options"
                @limit-change="onLimitChange"
                @page-change="onPageChange"
            />
        </b-card>

        <!-- Modal Tolak (Reject Modal) -->
        <b-modal
            id="m-reject-request"
            title="Tolak Request Edit Emas"
            no-close-on-backdrop
            no-close-on-esc
            hide-footer
            @hidden="clearRejectForm"
        >
            <b-form-group label-class="font-weight-bold">
                <template #label>
                    Alasan Penolakan <span style="color: red;">*</span>
                </template>
                <b-form-textarea
                    v-model="rejectForm.reject_note"
                    placeholder="Masukkan alasan penolakan"
                    rows="3"
                    maxlength="255"
                />
                <small class="text-muted">
                    {{
                        rejectForm.reject_note
                            ? rejectForm.reject_note.length
                            : 0
                    }}
                    / 255
                </small>
            </b-form-group>
            <div class="text-right mt-3">
                <b-button
                    variant="secondary"
                    @click="$bvModal.hide('m-reject-request')"
                    class="mr-2"
                >
                    Batal
                </b-button>
                <b-button
                    variant="danger"
                    :disabled="isSubmitting"
                    @click="submitRejectRequest"
                >
                    {{ isSubmitting ? "Memproses..." : "Tolak Request" }}
                </b-button>
            </div>
        </b-modal>
    </div>
</template>

<script>
import enumPermissions from "../../shared/enum-permissions";

const STATUS_RESET = Object.freeze({
    PENDING: 1,
    APPROVED: 2,
    REJECTED: 3
});

export default {
    data() {
        return {
            STATUS_RESET,
            initStatus: 0,
            isBusy: true,
            isSubmitting: false,
            show: 10,

            filter: {
                noFaktur: "",
                status: -1
            },

            options: [
                { value: 10, text: "10" },
                { value: 20, text: "20" },
                { value: 30, text: "30" },
                { value: 40, text: "40" },
                { value: 50, text: "50" }
            ],

            fields: [
                { key: "noFaktur", label: "No. Faktur", sortable: false },
                { key: "oldItem", label: "Data Lama", sortable: false },
                { key: "newItem", label: "Data Baru", sortable: false },
                { key: "created", label: "Tanggal Request", sortable: false },
                {
                    key: "status",
                    label: "Status",
                    sortable: false,
                    thClass: "text-center",
                    tdClass: "text-center"
                },
                {
                    key: "action",
                    label: "Aksi",
                    thClass: "text-center",
                    tdClass: "text-center"
                }
            ],

            items: [],
            activeItem: {
                id: null,
                inventory: {}
            },
            currentPage: 1,
            perPage: 10,
            totalRows: 0,
            sortBy: null,
            sortDesc: false,

            optFilterStatus: [
                { value: -1, text: "Semua" },
                { value: STATUS_RESET.PENDING, text: "PENDING" },
                { value: STATUS_RESET.APPROVED, text: "DITERIMA" },
                { value: STATUS_RESET.REJECTED, text: "DITOLAK" }
            ],

            rejectForm: {
                reject_note: ""
            }
        };
    },

    methods: {
        init() {
            this.initStatus = 0;
            let getItems = this.getItems();

            Promise.all([getItems])
                .then(() => {
                    this.initStatus = 1;
                })
                .catch(_ => {
                    this.initStatus = -1;
                });
        },

        getItems(page = 1) {
            this.isBusy = true;
            this.items = [];

            if (isNaN(page)) {
                this.currentPage = 1;
            } else {
                this.currentPage = page;
            }

            // URL builder
            let param = this.$helper.getBasePagedQuery(
                this.currentPage,
                this.perPage
            );

            if (!this.$helper.isBlank(this.filter.noFaktur)) {
                param += "&filterNoFaktur=" + this.filter.noFaktur;
            }

            if (this.filter.status != -1) {
                param += "&filterStatus=" + this.filter.status;
            }

            if (!this.$helper.isBlank(this.sortBy)) {
                param += "&sortBy=" + this.sortBy;
                param += "&sortDesc=" + this.sortDesc;
            }

            return this.$axios
                .get("gold-reset-requests" + param)
                .then(res => {
                    this.items = res.data.data.items;
                    this.totalRows = res.data.data.total;
                    sessionStorage.setItem(
                        "listInventoryResetFilter",
                        JSON.stringify(this.filter)
                    );
                })
                .catch(error => {
                    this.$helper.parseError(this, error);
                })
                .finally(() => {
                    this.isBusy = false;
                });
        },

        processRequest(item, status, rejectNote = null) {
            this.isSubmitting = true;
            const payload = { status };
            if (rejectNote) {
                payload.reject_note = rejectNote;
            }

            this.$axios
                .post(
                    "gold-reset-requests/" + item.id + "/process",
                    payload
                )
                .then(() => {
                    const isApprove = status === STATUS_RESET.APPROVED;
                    this.$helper.toastSuccess(
                        this,
                        isApprove
                            ? "Request berhasil diapprove!"
                            : "Request berhasil ditolak!"
                    );
                    if (!isApprove) {
                        this.$bvModal.hide("m-reject-request");
                    }
                    this.getItems(this.currentPage);
                })
                .catch(error => this.$helper.parseError(this, error))
                .finally(() => (this.isSubmitting = false));
        },

        acceptRequest(item) {
            this.activeItem = item;

            let isConfirmed = confirm(
                `Lakukan approval untuk request reset NF ${item.no_faktur_pgi}?`
            );
            if (!isConfirmed) return;

            this.processRequest(item, STATUS_RESET.APPROVED);
        },

        rejectRequest(item) {
            this.activeItem = item;
            this.rejectForm.reject_note = "";
            this.$bvModal.show("m-reject-request");
        },

        clearRejectForm() {
            this.rejectForm.reject_note = "";
        },

        resetFilter() {
            sessionStorage.removeItem("listInventoryResetFilter");

            this.filter.noFaktur = "";
            this.filter.status = -1;

            this.getItems();
        },

        submitRejectRequest() {
            const note = this.rejectForm.reject_note
                ? this.rejectForm.reject_note.trim()
                : "";
            if (!note) {
                this.$helper.toastError(this, "Alasan penolakan wajib diisi!");
                return;
            }
            if (note.length > 255) {
                this.$helper.toastError(
                    this,
                    "Alasan penolakan maksimal 255 karakter!"
                );
                return;
            }

            this.processRequest(this.activeItem, STATUS_RESET.REJECTED, note);
        },

        onLimitChange(newPerPage) {
            this.perPage = newPerPage;
            this.getItems(this.currentPage);
        },

        onPageChange(newPage) {
            this.currentPage = newPage;
            this.getItems(this.currentPage);
        },

        onSortChanged(ctx) {
            this.sortBy = ctx.sortBy;
            this.sortDesc = ctx.sortDesc;
            this.getItems();
        },

        getStatusBadge(status) {
            switch (status) {
                case STATUS_RESET.PENDING:
                    return ["warning", "PENDING"];
                case STATUS_RESET.APPROVED:
                    return ["success", "DITERIMA"];
                case STATUS_RESET.REJECTED:
                    return ["danger", "DITOLAK"];
                default:
                    return ["light", "-"];
            }
        },

        showInfo(item) {
            this.$router.push("/emas/edit-emas/" + item.id);
        },
        canApprove(item) {
            return (
                item.status === STATUS_RESET.PENDING &&
                this.hasPermission(enumPermissions.APPROVE_RESET_INVENTORY_GOLD)
            );
        },
        hasPermission(permissionId) {
            return (
                this.$helper.checkRoleNumber(
                    permissionId.toString(),
                    localStorage.getItem("permissions")
                ) || localStorage.getItem("roleName").includes("Superadmin")
            );
        }
    },

    created() {
        if (
            !this.$helper.isBlank(
                sessionStorage.getItem("listInventoryResetFilter")
            )
        ) {
            this.filter = JSON.parse(
                sessionStorage.getItem("listInventoryResetFilter")
            );
        }
        this.init();
    }
};
</script>
